import React, { useState, useEffect } from 'react';
import { Cookie, X, CheckCircle } from 'lucide-react';
import { Link } from 'react-router-dom';

const CookieConsent = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    // Vérifier d'abord si l'âge est vérifié
    const ageVerified = localStorage.getItem('ageVerified');
    if (!ageVerified) {
      return; // Ne pas afficher le cookie consent si l'âge n'est pas vérifié
    }

    // Vérifier si l'utilisateur a déjà accepté/refusé les cookies
    const cookieConsent = localStorage.getItem('cookieConsent');
    if (!cookieConsent) {
      // Attendre un peu avant d'afficher pour ne pas être trop intrusif
      const timer = setTimeout(() => {
        setShowBanner(true);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAcceptAll = () => {
    localStorage.setItem('cookieConsent', 'accepted');
    localStorage.setItem('cookieConsentDate', new Date().toISOString());
    setShowBanner(false);
  };

  const handleAcceptNecessary = () => {
    localStorage.setItem('cookieConsent', 'necessary');
    localStorage.setItem('cookieConsentDate', new Date().toISOString());
    setShowBanner(false);
  };

  const handleClose = () => {
    localStorage.setItem('cookieConsent', 'dismissed');
    localStorage.setItem('cookieConsentDate', new Date().toISOString());
    setShowBanner(false);
  };

  if (!showBanner) return null;

  return (
    <div className="fixed bottom-4 right-4 z-40 max-w-sm animate-slide-up">
      <div className="glass-strong rounded-lg p-3 md:p-4 border border-toxic-green/30 shadow-2xl">
        {!isExpanded ? (
          // Vue compacte
          <div className="flex items-start gap-2">
            <Cookie className="w-5 h-5 text-toxic-green flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="text-xs text-white/90 mb-2 leading-relaxed">
                Nous utilisons des cookies.{' '}
                <Link to="/cookies" data-discover="true" className="text-toxic-green hover:text-toxic-cyan underline">
                  En savoir plus
                </Link>
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleAcceptAll}
                  className="px-3 py-1.5 bg-gradient-to-r from-toxic-green to-toxic-cyan text-black text-xs font-bold rounded hover:shadow-lg transition-all whitespace-nowrap"
                >
                  Accepter
                </button>
                <button
                  onClick={() => setIsExpanded(true)}
                  className="px-3 py-1.5 glass border border-white/20 text-white text-xs font-semibold rounded hover:bg-white/10 transition-all whitespace-nowrap"
                >
                  Options
                </button>
                <button
                  onClick={handleClose}
                  className="p-1 text-white/60 hover:text-white transition-colors flex-shrink-0"
                  aria-label="Fermer"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        ) : (
          // Vue étendue avec options
          <div>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Cookie className="w-4 h-4 text-toxic-green" />
                <h3 className="text-sm font-bold text-white">Préférences</h3>
              </div>
              <button
                onClick={() => setIsExpanded(false)}
                className="text-white/60 hover:text-white transition-colors"
                aria-label="Réduire"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <p className="text-xs text-white/80 mb-3 leading-relaxed">
              Choisissez les cookies à accepter.{' '}
              <Link to="/cookies" className="text-toxic-green hover:text-toxic-cyan underline">
                En savoir plus
              </Link>
            </p>

            <div className="space-y-2 mb-3">
              <div className="flex items-center justify-between p-2 glass rounded text-xs">
                <div className="flex-1">
                  <p className="font-semibold text-white">Nécessaires</p>
                  <p className="text-white/60 text-xs">Toujours actifs</p>
                </div>
                <CheckCircle className="w-4 h-4 text-toxic-green flex-shrink-0" />
              </div>

              <div className="flex items-center justify-between p-2 glass rounded text-xs">
                <div className="flex-1">
                  <p className="font-semibold text-white">Performance</p>
                  <p className="text-white/60 text-xs">Améliore le site</p>
                </div>
                <div className="w-10 h-5 bg-toxic-green/30 rounded-full relative cursor-pointer flex-shrink-0" onClick={handleAcceptAll}>
                  <div className="w-4 h-4 bg-toxic-green rounded-full absolute right-0.5 top-0.5 transition-transform"></div>
                </div>
              </div>

              <div className="flex items-center justify-between p-2 glass rounded text-xs">
                <div className="flex-1">
                  <p className="font-semibold text-white">Ciblage</p>
                  <p className="text-white/60 text-xs">Publicité</p>
                </div>
                <div className="w-10 h-5 bg-white/20 rounded-full relative cursor-pointer flex-shrink-0">
                  <div className="w-4 h-4 bg-white/40 rounded-full absolute left-0.5 top-0.5 transition-transform"></div>
                </div>
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <button
                onClick={handleAcceptAll}
                className="px-3 py-1.5 bg-gradient-to-r from-toxic-green to-toxic-cyan text-black text-xs font-bold rounded hover:shadow-lg transition-all"
              >
                Tout accepter
              </button>
              <button
                onClick={handleAcceptNecessary}
                className="px-3 py-1.5 glass border border-white/20 text-white text-xs font-semibold rounded hover:bg-white/10 transition-all"
              >
                Uniquement nécessaires
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CookieConsent;

